import classNames from 'classnames';
import getContrast from '@alltrails/core/utils/getContrast';
import { COLOR_BACKGROUND_PRIMARY } from '@alltrails/denali/tokens';
import Typography from '@alltrails/core/components/Typography';
import { useIntl } from 'react-intl';
import { useCallback } from 'react';
import { getConvertedDistance } from '@alltrails/data-formatting/distanceConverters';
import TrailSurfaceTypeAggregation from '../../types/TrailSurfaceTypeAggregation';
import styles from './SurfaceTypeBreakdown.module.scss';

const needsBorder = (hex: string) => {
  try {
    return getContrast(hex, COLOR_BACKGROUND_PRIMARY) < 1.5;
  } catch {
    return false;
  }
};

type SurfaceTypeBreakdownProps = {
  className?: string;
  showVisualBreakdown?: boolean;
  surfaceTypeAggregation: TrailSurfaceTypeAggregation;
  useMetric?: boolean;
  verticalSpacing?: 'default' | 'compact';
};

const SurfaceTypeBreakdown = ({
  className,
  showVisualBreakdown = false,
  surfaceTypeAggregation,
  useMetric = false,
  verticalSpacing = 'default'
}: SurfaceTypeBreakdownProps) => {
  const intl = useIntl();

  const getFormattedPercent = useCallback((percent: number) => {
    // If the percent would be rounded to 0, we want to show <1% instead
    if (percent < 0.5) {
      return `<1%`;
    }
    return `${Math.round(percent)}%`;
  }, []);

  const getFormattedDistance = useCallback(
    (meters: number) => {
      let distance = getConvertedDistance(meters, useMetric);
      let leadingCharacter = '';
      // If the distance would be rounded to 0.0, we want to show <0.1 instead
      if (distance < 0.05) {
        distance = 0.1;
        leadingCharacter = '<';
      }
      return `${leadingCharacter}${intl.formatNumber(distance, {
        style: 'unit',
        unit: useMetric ? 'kilometer' : 'mile',
        notation: 'compact',
        maximumFractionDigits: 1
      })}`;
    },
    [intl, useMetric]
  );

  if (!surfaceTypeAggregation.length) {
    return null;
  }

  return (
    <div className={classNames(styles.container, styles[verticalSpacing], className)}>
      {showVisualBreakdown ? (
        <div className={styles.barContainer}>
          {surfaceTypeAggregation.map(({ percentOfSurface, surfaceType }) => (
            <div
              key={`surface-type-bar-${surfaceType.identifier}`}
              className={classNames(styles.bar, { [styles.bordered]: needsBorder(surfaceType.color) })}
              style={{ backgroundColor: surfaceType.color, flexBasis: `${percentOfSurface}%` }}
            />
          ))}
        </div>
      ) : null}
      <div className={styles.dataContainer}>
        {surfaceTypeAggregation.map(({ percentOfSurface, surfaceType, totalLength }) => (
          <div key={`surface-type-${surfaceType.identifier}`} className={styles.dataRow}>
            <div
              className={classNames(styles.swatch, { [styles.bordered]: needsBorder(surfaceType.color) })}
              style={{ backgroundColor: surfaceType.color }}
            />
            <Typography className={styles.dataLabel} variant="text200">
              {surfaceType.label}
            </Typography>
            <div>
              <Typography color="secondary" variant="text100">
                {getFormattedPercent(percentOfSurface)}
              </Typography>
              <Typography color="tertiary" variant="text100">
                {' / '}
              </Typography>
              <Typography color="secondary" variant="text100">
                {getFormattedDistance(totalLength)}
              </Typography>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SurfaceTypeBreakdown;

export const SurfaceTypeBreakdownLoading = ({
  className,
  showVisualBreakdown = false,
  verticalSpacing = 'default'
}: Pick<SurfaceTypeBreakdownProps, 'className' | 'showVisualBreakdown' | 'verticalSpacing'>) => {
  return (
    <div className={classNames(styles.container, styles[verticalSpacing], className)}>
      {showVisualBreakdown ? (
        <div className={styles.barContainer}>
          <div className={classNames(styles.bar, styles.loading)} />
        </div>
      ) : null}
      <div className={styles.dataContainer}>
        {Array.from({ length: 3 }, (_, i) => (
          <div key={`data-row-${i}`} className={styles.dataRow}>
            <div className={classNames(styles.swatch, styles.loading)} />
            <Typography className={classNames(styles.dataLabel, styles.loading)} variant="text200">
              &nbsp;
            </Typography>
            <Typography className={classNames(styles.dataValue, styles.loading)} variant="text100">
              &nbsp;
            </Typography>
          </div>
        ))}
      </div>
    </div>
  );
};
